//Modal to grant kiosk access to a worker at a jobsite, and to revoke kiosk access. 


import React, { useState, useEffect } from 'react';
import { FaSearch } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { getWorkers } from '../features/workers/workerSlice';
import { toast } from 'react-toastify';
import { getKioskAccessList, grantKioskAccess, revokeKioskAccess } from '../features/kioskManagement/kioskManagementSlice';

const KioskAccessModal = ({ isOpen, onClose, jobsiteId }) => {
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();
  const { workers, isLoading, message } = useSelector((state) => state.workers);
  const { jobsites } = useSelector((state) => state.businessJobsites);
  const { kioskAccess, isLoading: kioskLoading, message: kioskMessage } = useSelector((state) => state.kioskManagement);

  useEffect(() => {
    if (isOpen) {
      dispatch(getWorkers());
      if (jobsiteId) {
        dispatch(getKioskAccessList(jobsiteId));
      }
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (message) {
      toast(message);
    }
  }, [message]);

  useEffect(() => {
    if (kioskMessage) {
      toast(kioskMessage);
    }
  }, [kioskMessage]);

  // Find the current jobsite and its foremen
  const currentJobsite = Array.isArray(jobsites)
    ? jobsites.find(js => js.id === jobsiteId)
    : null;
  const foremen = currentJobsite?.foremen || [];

  const filteredWorkers = Array.isArray(workers)
    ? workers
        // Exclude workers who already have kiosk access
        .filter((worker) => {
          const id = worker.id || worker.workerId;
          return !Array.isArray(kioskAccess) || !kioskAccess.some((w) => (w.id || w.workerId) === id);
        })
        // Apply search filter
        .filter((worker) => {
          const term = search.toLowerCase();
          return (
            worker.name.toLowerCase().includes(term) ||
            worker.lastName.toLowerCase().includes(term)
          );
        })
    : [];

 

  const handleGrantAccess = (workerId) => {
    if (!jobsiteId || !workerId) return;
    dispatch(grantKioskAccess({ jobsiteId, workerId }));
  };

  const handleRevokeAccess = (workerId) => {
    if (!jobsiteId || !workerId) return;
    dispatch(revokeKioskAccess({ jobsiteId, workerId }));
  };

  if (!isOpen) return null;

  return (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
	  
    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full md:w-4/5 lg:w-3/4 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-2">
          <h2 className="text-lg font-bold text-blue-700">Kiosk Access</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-red-500 text-xl leading-none"
            aria-label="Close kiosk access modal"
          >
            X
          </button>
        </div>
        <p className="mb-4 text-sm text-gray-700">
          Workers listed below may use the kiosk feature to clock in and clock out other workers at this jobsite.
        </p>

        {/* Add worker selector */}
        <div className="mb-6 border-b pb-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Add worker</h3>
          <div className="mb-2">
            <div className="relative">
              <FaSearch className="absolute left-2 top-2.5 text-gray-400 text-xs" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search workers by name"
                className="w-full pl-7 pr-2 py-1 border rounded text-sm"
              />
            </div>
          </div>

          <div className="max-h-40 overflow-y-auto border rounded">
            {Array.isArray(filteredWorkers) && filteredWorkers.length > 0 ? (
              filteredWorkers.map((worker) => {
                const id = worker.id || worker.workerId;
                return (
                  <div
                    key={id}
                    className="w-full flex items-center justify-between px-2 py-1 text-left text-sm hover:bg-gray-50 group"
                  >
                    <span>
                      {worker.name} {worker.lastName}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleGrantAccess(id)}
                      className="opacity-0 group-hover:opacity-100 text-xs text-white bg-blue-800 px-2 py-1 rounded transition-opacity"
                    >
                      Grant kiosk access
                    </button>
                  </div>
                );
              })
            ) : (
              <p className="text-xs text-gray-500 px-2 py-1">No workers found.</p>
            )}
          </div>
        </div>

        {/* Existing kiosk access list */}
        <div className="mb-4">
          {kioskLoading ? (
            <p className="text-sm text-gray-500">Loading kiosk access...</p>
          ) : kioskAccess && kioskAccess.length > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Workers with kiosk access</h3>
              <ul className="text-sm text-gray-700 max-h-40 overflow-y-auto divide-y">
                {kioskAccess.map((worker) => {
                  const id = worker.id || worker.workerId;
                  const avatar = worker.profilePicture || worker.profilePictureURL;
                  return (
          <li
            key={id}
            className="flex items-center py-2 px-1 group"
          >
            {avatar ? (
            <img
              src={avatar}
              alt={`${worker.name} ${worker.lastName}`}
              className="w-8 h-8 rounded-full object-cover border"
            />
            ) : (
            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-semibold text-gray-700">
              {worker.name?.[0]}
              {worker.lastName?.[0]}
            </div>
            )}
            <span className="ml-2">
            {worker.name} {worker.lastName}
            </span>
            <button
            type="button"
            onClick={() => handleRevokeAccess(id)}
            className="ml-auto opacity-0 group-hover:opacity-100 text-xs text-white bg-red-700 px-2 py-1 rounded hover:bg-red-800 transition-opacity"
            >
            Revoke access
            </button>
          </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No workers have kiosk access.</p>
          )}
        </div>
        
      </div>
    </div>
  );
};

export default KioskAccessModal;
